import {DialogActions, Typography} from "@mui/material";
import {Grid, useTheme} from "@mui/system";
import {CustomModal, ProcessDatePicker} from "components";
import pxToRem from "hooks/usePxToRem";
import {useEffect, useRef, useState} from "react";
import {
  Controller,
  FieldErrors,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import {CustomAutocomplete, CustomButton} from "ui";
import {AutoCompleteData} from "ui/CustomAutoComplete/CustomAutoComplete";
import useHandleBoardingOptions from "views/ProcessDetails/refactor/components/ProcessBoarding/useHandleBoardingOptions";
import useChangeDAteAndAutocomplete from "views/ProcessDetails/hooks/useChangeDAteAndAutocomplete";
import useHandleFieldLists from "views/ProcessDetails/hooks/useHandleFieldLists";
import {getTooltipText} from "views/ProcessDetails/utils/getTooltipText";
import {
  GetTransportCompaniesList,
  GetVesselsList,
} from "services/api_v2/Processes/Processes.service";
import useVoyageTypes from "../useVoyageTypes";
import {
  ID_COLLECT_VOYAGE,
  ID_DELIVERY_VOYAGE,
  ID_FINAL_DESTINATION_VOYAGE,
  ID_INITIAL_VOYAGE,
  ID_RECEIVING_LOCATION,
  ID_TRANSHIPMENT_TYPE_VOYAGE,
} from "views/ProcessDetails/utils/constants";
import useValidateVoyages from "views/ProcessDetails/hooks/useValidateVoyages";
import {brazilianTimeZoneCurrentDate, timeZone} from "global/constants";
import {utcToZonedTime} from "date-fns-tz";
import {add} from "date-fns";
import useAlertMessage from "hooks/useAlertMessage";
import ProcessTextField from "views/ProcessDetails/refactor/components/ProcessTextField";
import {Process} from "views/ProcessDetails/refactor/types/process.types";
import {IdAndName} from "types/GenericTypes";
import {VoyageTypeService} from "util/globalEnums";
import ProcessSelect from "views/ProcessDetails/refactor/components/ProcessSelect";
import {Voyage as VoyageRefactor} from "views/ProcessDetails/refactor/types/voyages.types";
import {Voyage} from "views/ProcessDetails/Process.types";
import {getLocationType} from "views/ProcessDetails/refactor/components/ProcessBoarding/components/VoyagesV2/utils/getLocationType";

export type AddOrEditVoyageData = {
  id?: number;
  typeVoyage?: null | {id?: number | null; name?: string | null};
  origin?: null | {id?: number | null; name?: string | null};
  destination?: null | {id?: number | null; name?: string | null};
  vessel?: null | {id?: number | null; name?: string | null};
  voyageFlight?: string;
  estimatedCargoLoad?: string;
  cargoLoad?: string;
  estimatedCargoUnload?: string;
  cargoUnload?: string;
  placePickup?: string;
  placeDelivery?: string;
  precedence?: number;
  typeService?: VoyageTypeService | null;
  bindingDate?: string;
  grantDate?: string;
  transportCompany?: {person: {id: number; name: string}} | null;
  observation?: string | null;
};

type AddOrEditVoyageModalProps = {
  isOpen: boolean;
  index: number | null;
  handleSubmitVoyage: (data: AddOrEditVoyageData, dirty) => void;
  newVoyage?: "" | "newVoyage";
  handleCloseModal: () => void;
  modalData: AddOrEditVoyageData;
  initialOrigin: IdAndName | undefined;
  initialDestination?: IdAndName;
  allVoyages: VoyageRefactor[];
};
const AddOrEditVoyageModal = (props: AddOrEditVoyageModalProps) => {
  const {mountOptions} = useChangeDAteAndAutocomplete();
  const {control: processControl, watch: watchProcess} =
    useFormContext<Process>();
  const {fields} = useFieldArray({
    name: "boarding.voyages",
    keyName: "formId",
    control: processControl,
  });
  const formRef = useRef<HTMLFormElement | null>(null);
  const formId = "newVoyage";

  const defaultValues: AddOrEditVoyageData = {
    ...props.modalData,
  };
  const {handleSubmit, control, setValue, formState, watch, getValues} =
    useForm({
      defaultValues: defaultValues,
    });

  const isAiProcess =
    `${watchProcess("operation")?.[0]}${watchProcess("modality")?.[0]}` ===
    "IA";

  const [boardingOptions, setBoardingOptions] = useState(
    [] as AutoCompleteData[],
  );
  const [vesselsOptions, setVesselsOptions] = useState(
    [] as AutoCompleteData[],
  );

  const [voyageTypes, setVoyageTypes] = useState([] as AutoCompleteData[]);

  const {getVoyageTypes, loading: loadingVoyageTypes} = useVoyageTypes();

  const [showAlert] = useAlertMessage();

  const {
    getOptions: getBoardingOptions,
    onBottomScroll: onBottomScrollBoarding,
    isLoadingOptions: isLoadingBoardingOptions,
  } = useHandleBoardingOptions();

  const {
    getOptions: getVesselsOptions,
    onBottomScroll: onBottomScrollVessels,
    isLoadingOptions: isLoadingVesselsOptions,
  } = useHandleFieldLists(GetVesselsList);

  const [searchFields, setSearchFields] = useState({
    vessels: "",
    origin: "",
    destination: "",
  });

  const handleCloseModal = () => {
    props.handleCloseModal();
  };

  const [showHelperText, setShowHelperText] = useState({
    typeVoyage: false,
    origin: false,
    destination: false,
  });

  const {dirtyFields} = formState;
  const onSubmit = (data: AddOrEditVoyageData) => {
    props.handleSubmitVoyage(data, dirtyFields);
  };

  const onInvalid = (
    errors: FieldErrors<{processDate: {[key: string]: {message: string}}}>,
  ) => {
    let message = "Campos inválidos: ";

    for (const errorKey in errors) {
      if (Array.isArray(errors[errorKey])) {
        for (const e in errors[errorKey]) {
          if (errors[errorKey][e]?.destination?.message) {
            message += `${errors[errorKey][e]?.destination?.message}`;
          }
          if (errors[errorKey][e]?.cargoLoad?.message) {
            message += `${errors[errorKey][e]?.cargoLoad?.message}`;
          }
        }
      } else {
        message += `${errors[errorKey]?.message}`;
      }
    }

    showAlert(message);
  };

  const handleChangeTypeVoyageToCollect = (
    _: React.SyntheticEvent<Element, Event>,
    value: any,
  ) => {
    if (value?.label?.toLowerCase() === "coleta") {
      setValue("id", props.modalData?.id, {shouldDirty: true});

      setValue("origin", null, {shouldDirty: true});
      setValue("destination", props?.initialDestination, {shouldDirty: true});
      setValue(
        "typeVoyage",
        {
          id: value?.id,
          name: value?.label,
        },
        {shouldDirty: true},
      );
      return;
    }

    if (value?.id === ID_TRANSHIPMENT_TYPE_VOYAGE) {
      const lastTranshipmentOrInitialVoyage = props?.allVoyages?.findLast(
        (voyage) =>
          voyage.typeVoyage?.id === ID_TRANSHIPMENT_TYPE_VOYAGE ||
          voyage.typeVoyage?.id === ID_INITIAL_VOYAGE,
      );
      setValue("origin", lastTranshipmentOrInitialVoyage?.destination, {
        shouldDirty: true,
      });
      setValue(
        "typeVoyage",
        {
          id: value?.id,
          name: value?.label,
        },
        {shouldDirty: true},
      );
      return;
    }

    const lastVoyage = allVoyages[allVoyages.length - 1];

    if (value?.label?.toLowerCase() === "local recebimento") {
      const defaultOrigin =
        props?.allVoyages?.find(
          (voyage) => voyage.typeVoyage?.id === ID_COLLECT_VOYAGE,
        )?.destination ?? null;

      setValue(
        "typeVoyage",
        {id: value?.id, name: value?.label},
        {shouldDirty: true},
      );

      setValue("origin", defaultOrigin, {shouldDirty: true});
      setValue("destination", props?.initialDestination, {shouldDirty: true});

      return;
    }

    setValue("origin", lastVoyage?.destination ?? null, {shouldDirty: true});
    setValue("destination", null, {shouldDirty: true});
    setValue(
      "typeVoyage",
      {id: value?.id, name: value?.label},
      {shouldDirty: true},
    );
  };

  const handleChangeAutocomplete = (
    _: React.SyntheticEvent<Element, Event>,
    value: any,
    name: any,
  ) => {
    if (value === null) {
      setValue(name, {id: null, name: null}, {shouldDirty: true});
      return;
    }
    setValue(name, {id: value?.id, name: value?.label}, {shouldDirty: true});
  };

  const handleFormSubmissionWithValidation = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (formRef?.current) {
      const isValid = formRef?.current?.reportValidity();
      if (!isValid) {
        return;
      } else {
        handleSubmit(onSubmit, onInvalid)(e);
      }
    }
  };

  useEffect(() => {
    if (props.initialOrigin) {
      setValue("origin", props.initialOrigin, {shouldDirty: true});
    }
  }, []);

  const voyageNumber = () => {
    if (props.newVoyage && fields?.length) {
      return fields?.length + 1;
    } else if (fields?.length) {
      return Number(props?.index) + 1;
    } else {
      return 1;
    }
  };
  const allVoyages = useWatch({name: `boarding.voyages`});

  const {
    minDateVoyageLoad,
    shouldDisableLoad,
    shouldDisableUnload,
    minDateVoyageUnload,
    shouldDisableOrigin,
    dateIsFarFromOneYear,
  } = useValidateVoyages();

  const modalVoyage = watch();
  const theme = useTheme();

  const collectVoyageMaxDate =
    props?.modalData?.typeVoyage?.id === ID_COLLECT_VOYAGE
      ? utcToZonedTime(
          add(
            new Date(
              fields?.find((item) => item?.typeVoyage?.id === ID_INITIAL_VOYAGE)
                ?.cargoLoad ?? "",
            ),
            {hours: 3},
          ),
          timeZone,
        )
      : undefined;

  const shouldShowVessel = (voyage: Voyage) => {
    if (
      voyage.typeVoyage?.id === ID_COLLECT_VOYAGE ||
      voyage.typeVoyage?.id === ID_FINAL_DESTINATION_VOYAGE ||
      voyage.typeVoyage?.id === ID_RECEIVING_LOCATION
    ) {
      return false;
    }
    if (isAiProcess) {
      return false;
    }
    return true;
  };

  const shouldShowPlaceDelivery = (voyage: Voyage) => {
    if (voyage.typeVoyage?.id === ID_DELIVERY_VOYAGE) {
      return true;
    }

    if (voyage.typeVoyage?.id === ID_FINAL_DESTINATION_VOYAGE) {
      return true;
    }

    return false;
  };

  const shouldShowTransportCompany = (voyage: Voyage) => {
    if (voyage.typeVoyage?.id === ID_FINAL_DESTINATION_VOYAGE && isAiProcess) {
      return true;
    }
  };

  const shouldShowObservation = (voyage: Voyage) => {
    if (voyage.typeVoyage?.id === ID_FINAL_DESTINATION_VOYAGE) {
      return true;
    }
  };

  const shouldShowBindingAndGrantDates = (voyage: Voyage) => {
    if (voyage.typeVoyage?.id === ID_FINAL_DESTINATION_VOYAGE && isAiProcess) {
      return true;
    }
    return false;
  };

  const getTypeVoyageHelperText = () => {
    if (!getValues(`typeVoyage`)?.id) {
      return "Preencha o tipo de viagem.";
    }

    return showHelperText.typeVoyage && "Preencha este campo.";
  };

  const formatTransportCompanyFn = (data) => {
    return data?.data?.map((item) => ({
      label: `${item?.person?.name} - ${item?.idPerson}`,
      id: item?.person?.id,
    }));
  };

  const {
    getOptions: getTransportCompanyOptions,
    onBottomScroll: onBottomScrollTransportCompany,
    isLoadingOptions: isLoadingTransportCompanyOptions,
  } = useHandleFieldLists(GetTransportCompaniesList, formatTransportCompanyFn);

  const [transportCompanyOptions, setTransportCompanyOptions] = useState(
    [] as AutoCompleteData[],
  );

  const [search, setSearch] = useState("");

  useEffect(() => {
    setBoardingOptions([]);
  }, [modalVoyage?.typeVoyage?.id]);

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={handleCloseModal}
      title={
        props.newVoyage ? "Adicionar nova viagem" : "Editar dados da viagem"
      }
      titleIcon={"directions_boat"}
      maxWidth="md"
    >
      <form ref={formRef}>
        <Grid
          container
          columnSpacing={pxToRem(8)}
          rowSpacing={pxToRem(20)}
          sx={{
            padding: pxToRem(20),
          }}
          id={formId}
        >
          <Grid size={12}>
            <Typography variant="subtitle1">{`Viagem ${voyageNumber()}`}</Typography>
          </Grid>
          <Grid size={3}>
            <Controller
              name={`typeVoyage`}
              control={control}
              render={({field: {value}}) => {
                return (
                  <CustomAutocomplete
                    name={`typeVoyage`}
                    value={value?.name ?? ""}
                    options={voyageTypes}
                    onOpen={() => getVoyageTypes(setVoyageTypes, isAiProcess)}
                    onChange={(e, value) =>
                      handleChangeTypeVoyageToCollect(e, value)
                    }
                    onBlur={() =>
                      setShowHelperText((prev) => ({
                        ...prev,
                        typeVoyage: false,
                      }))
                    }
                    placeholder="Tipo viagem"
                    loading={loadingVoyageTypes}
                    required
                    textFieldProps={{
                      label: "Tipo de viagem",
                      required: true,
                      helperText:
                        showHelperText.typeVoyage && "Preencha este campo.",
                      onInvalid: () =>
                        setShowHelperText((prev) => ({
                          ...prev,
                          typeVoyage: true,
                        })),
                    }}
                  />
                );
              }}
            />
          </Grid>
          <Grid size={3}>
            <Controller
              name={`origin`}
              control={control}
              render={({field: {value}}) => (
                <>
                  <CustomAutocomplete
                    name={`origin`}
                    required={modalVoyage?.typeVoyage?.id !== ID_COLLECT_VOYAGE}
                    placeholder="Origem"
                    optionSecondaryContent
                    tooltipText={getTooltipText(value?.name ?? "")}
                    loading={isLoadingBoardingOptions}
                    options={mountOptions(value, boardingOptions)}
                    value={value?.name ?? ""}
                    onOpen={() =>
                      getBoardingOptions(
                        setBoardingOptions,
                        searchFields.origin,
                        1,
                        50,
                        getLocationType(
                          isAiProcess,
                          modalVoyage?.typeVoyage?.id,
                        ),
                      )
                    }
                    disabled={
                      shouldDisableOrigin(
                        modalVoyage as unknown as VoyageRefactor,
                      )?.disabled
                    }
                    onInputChange={(e, value) => {
                      if (e !== null) {
                        setSearchFields((prev) => ({
                          ...prev,
                          origin: value,
                        }));
                        getBoardingOptions(
                          setBoardingOptions,
                          value,
                          1,
                          50,
                          getLocationType(
                            isAiProcess,
                            modalVoyage?.typeVoyage?.id,
                          ),
                        );
                      }
                    }}
                    getNextPage={() => {
                      onBottomScrollBoarding(
                        setBoardingOptions,
                        searchFields.origin,
                        getLocationType(
                          isAiProcess,
                          modalVoyage?.typeVoyage?.id,
                        ),
                      );
                    }}
                    onChange={(e, value) => {
                      handleChangeAutocomplete(e, value, `origin`);
                    }}
                    isOptionEqualToValue={(option, value) =>
                      value === option?.label
                    }
                    onBlur={() =>
                      setShowHelperText((prev) => ({...prev, origin: false}))
                    }
                    textFieldProps={{
                      label: "Origem",
                      required:
                        modalVoyage?.typeVoyage?.id !== ID_COLLECT_VOYAGE,
                      helperText:
                        showHelperText.origin && "Preencha este campo.",
                      onInvalid: () =>
                        setShowHelperText((prev) => ({...prev, origin: true})),
                    }}
                  />
                  {shouldDisableOrigin(modalVoyage as unknown as VoyageRefactor)
                    ?.message && (
                    <Typography
                      variant="subtitle3"
                      color={theme.palette.primary.main}
                    >
                      {
                        shouldDisableOrigin(
                          modalVoyage as unknown as VoyageRefactor,
                        )?.message
                      }
                    </Typography>
                  )}
                </>
              )}
            />
          </Grid>
          <Grid size={3}>
            <Controller
              name={`destination`}
              control={control}
              render={({field: {value}}) => (
                <CustomAutocomplete
                  name={`destination`}
                  placeholder="Destino"
                  optionSecondaryContent
                  tooltipText={getTooltipText(value?.name ?? "")}
                  loading={isLoadingBoardingOptions}
                  options={mountOptions(value, boardingOptions)}
                  value={value?.name ?? null}
                  onOpen={() => {
                    getBoardingOptions(
                      setBoardingOptions,
                      searchFields.destination,
                      1,
                      50,
                      getLocationType(isAiProcess, modalVoyage?.typeVoyage?.id),
                    );
                  }}
                  onInputChange={(e, value) => {
                    if (e !== null) {
                      setSearchFields((prev) => ({
                        ...prev,
                        destination: value,
                      }));
                      getBoardingOptions(
                        setBoardingOptions,
                        value,
                        1,
                        50,
                        getLocationType(
                          isAiProcess,
                          modalVoyage?.typeVoyage?.id,
                        ),
                      );
                    }
                  }}
                  getNextPage={() => {
                    onBottomScrollBoarding(
                      setBoardingOptions,
                      searchFields.destination,
                      getLocationType(isAiProcess, modalVoyage?.typeVoyage?.id),
                    );
                  }}
                  onChange={(e, value) => {
                    handleChangeAutocomplete(e, value, `destination`);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    value === option?.label
                  }
                  onBlur={() =>
                    setShowHelperText((prev) => ({
                      ...prev,
                      destination: false,
                    }))
                  }
                  textFieldProps={{
                    label: "Destino",
                    required: ![
                      ID_FINAL_DESTINATION_VOYAGE,
                      ID_DELIVERY_VOYAGE,
                    ].includes(modalVoyage?.typeVoyage?.id ?? 0),

                    helperText: getTypeVoyageHelperText(),
                    onInvalid: () =>
                      setShowHelperText((prev) => ({
                        ...prev,
                        destination: true,
                      })),
                  }}
                  disabled={!getValues(`typeVoyage`)?.id}
                  required={
                    ![ID_DELIVERY_VOYAGE].includes(
                      modalVoyage?.typeVoyage?.id ?? 0,
                    )
                  }
                />
              )}
            />
          </Grid>

          {shouldShowVessel(modalVoyage as Voyage) && (
            <Grid size={3}>
              <Controller
                name={`vessel`}
                control={control}
                render={({field: {value}}) => (
                  <CustomAutocomplete
                    name={`vessel`}
                    placeholder="Navio"
                    tooltipText={getTooltipText(value?.name ?? "")}
                    loading={isLoadingVesselsOptions}
                    options={mountOptions(value, vesselsOptions)}
                    value={value?.name ?? ""}
                    onOpen={() =>
                      getVesselsOptions(
                        setVesselsOptions,
                        1,
                        searchFields.vessels,
                      )
                    }
                    onInputChange={(e, value) => {
                      if (e !== null) {
                        setSearchFields((prev) => ({
                          ...prev,
                          vessels: value,
                        }));
                        getVesselsOptions(setVesselsOptions, 1, value);
                      }
                    }}
                    getNextPage={() => {
                      onBottomScrollVessels(
                        setVesselsOptions,
                        searchFields.vessels,
                      );
                    }}
                    onChange={(e, value) => {
                      handleChangeAutocomplete(e, value, `vessel`);
                    }}
                    isOptionEqualToValue={(option, value) =>
                      value === option?.label
                    }
                    textFieldProps={{label: "Navio"}}
                    required
                  />
                )}
              />
            </Grid>
          )}
          {!isAiProcess && (
            <Grid size={3}>
              <Controller
                name={`voyageFlight`}
                control={control}
                render={({field: {onChange, value}}) => (
                  <ProcessTextField
                    name={`voyageFlight`}
                    placeholder="Viagem"
                    value={value ?? ""}
                    onChange={onChange}
                    size="small"
                    label="Viagem"
                  />
                )}
              />
            </Grid>
          )}
          <Grid size={3}>
            <Controller
              name={`estimatedCargoLoad`}
              control={control}
              rules={{
                validate: {
                  dateIsFarFromOneYear: (value) => {
                    const dateError = dateIsFarFromOneYear(value ?? null);

                    if (dateError === "future") {
                      return `Data futura inválida para o ETD da viagem, superior 12 meses.`;
                    }

                    if (dateError === "past") {
                      return `Data passada inválida para o ETD da viagem, superior a 12 meses.`;
                    }

                    if (dateError === "invalid") {
                      return `Data inválida para o ETD da viagem.`;
                    }

                    return true;
                  },
                },
              }}
              render={({field: {value}}) => (
                <ProcessDatePicker
                  value={value}
                  name={`estimatedCargoLoad`}
                  title="ETD"
                  required={modalVoyage?.typeVoyage?.id !== ID_COLLECT_VOYAGE}
                  setValue={setValue}
                  maxDate={collectVoyageMaxDate}
                  label="ETD"
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid size={3}>
            <Controller
              name={`cargoLoad`}
              control={control}
              render={({field: {value}}) => (
                <>
                  <ProcessDatePicker
                    value={value}
                    name={`cargoLoad`}
                    title="Embarque"
                    setValue={setValue}
                    fullWidth
                    minDate={
                      minDateVoyageLoad(props.index ?? 0, allVoyages)?.date
                    }
                    maxDate={
                      collectVoyageMaxDate ?? brazilianTimeZoneCurrentDate
                    }
                    // Embarcar viagem apenas via atividade
                    disabled={true}
                    helperText={
                      minDateVoyageLoad(props.index ?? 0, allVoyages)
                        ?.message ?? "Data superior ao dia de hoje"
                    }
                    label="Embarque"
                  />
                  <Typography
                    variant="subtitle3"
                    color={theme.palette.primary.main}
                  >
                    Embarcar viagem via atividade
                  </Typography>
                </>
              )}
            />
          </Grid>
          <Grid size={3}>
            <Controller
              name={`estimatedCargoUnload`}
              control={control}
              rules={{
                validate: {
                  dateIsFarFromOneYear: (value) => {
                    const dateError = dateIsFarFromOneYear(value ?? null);

                    if (dateError === "future") {
                      return `Data futura inválida para o ETA da viagem, superior a 12 meses.`;
                    }

                    if (dateError === "past") {
                      return `Data passada inválida para o ETA da viagem, superior a 12 meses.`;
                    }

                    if (dateError === "invalid") {
                      return `Data inválida para o ETA da viagem.`;
                    }

                    return true;
                  },
                },
              }}
              render={({field: {value}}) => (
                <ProcessDatePicker
                  value={value}
                  name={`estimatedCargoUnload`}
                  errorName="Eta"
                  title="ETA"
                  required={modalVoyage?.typeVoyage?.id !== ID_COLLECT_VOYAGE}
                  setValue={setValue}
                  maxDate={collectVoyageMaxDate}
                  label="ETA"
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid size={3}>
            <Controller
              name={`cargoUnload`}
              control={control}
              render={({field: {value}}) => (
                <>
                  <ProcessDatePicker
                    value={value}
                    name={`cargoUnload`}
                    title="Desembarque"
                    setValue={setValue}
                    minDate={
                      minDateVoyageUnload(props.index ?? 0, [
                        ...allVoyages,
                        modalVoyage,
                      ])?.date
                    }
                    maxDate={
                      collectVoyageMaxDate ?? brazilianTimeZoneCurrentDate
                    }
                    helperText={
                      minDateVoyageUnload(props.index ?? 0, allVoyages)
                        ?.message || "Data futura inválida"
                    }
                    // Desembarcar viagem apenas via atividade
                    disabled={true}
                    label="Desembarque"
                    fullWidth
                  />

                  <Typography
                    variant="subtitle3"
                    color={theme.palette.primary.main}
                  >
                    Desembarcar viagem via atividade
                  </Typography>
                </>
              )}
            />
          </Grid>

          {getValues(`typeVoyage`)?.id === ID_COLLECT_VOYAGE && (
            <Grid size={3}>
              <Controller
                name={`placePickup`}
                control={control}
                render={({field: {onChange, value}}) => (
                  <ProcessTextField
                    name={`placePickup`}
                    placeholder="Local de coleta"
                    value={value ?? ""}
                    onChange={onChange}
                    size="small"
                    label="Local de coleta"
                  />
                )}
              />
            </Grid>
          )}

          {shouldShowBindingAndGrantDates(modalVoyage as Voyage) && (
            <Grid size={3}>
              <Controller
                name={`bindingDate`}
                control={control}
                render={({field: {value}}) => (
                  <ProcessDatePicker
                    value={value}
                    name={`bindingDate`}
                    errorName="Binding Date"
                    fullWidth
                    label="Vinculação DTA"
                    setValue={setValue}
                  />
                )}
              />
            </Grid>
          )}

          {shouldShowBindingAndGrantDates(modalVoyage as Voyage) && (
            <Grid size={3}>
              <Controller
                name={`grantDate`}
                control={control}
                render={({field: {value}}) => (
                  <ProcessDatePicker
                    value={value}
                    name={`grantDate`}
                    errorName="Grant Date"
                    fullWidth
                    label="Concessão DTA"
                    setValue={setValue}
                  />
                )}
              />
            </Grid>
          )}

          {isAiProcess && (
            <Grid size={3}>
              <Controller
                name={`typeService`}
                control={control}
                render={({field: {onChange, value}}) => (
                  <ProcessSelect
                    data={Object.values(VoyageTypeService)}
                    label="Tipo de serviço"
                    value={value ?? VoyageTypeService.NaoInformado}
                    name={`typeService` as `typeService`}
                    onChange={onChange}
                    size="small"
                  />
                )}
              />
            </Grid>
          )}

          {shouldShowPlaceDelivery(modalVoyage as Voyage) && (
            <Grid size={3}>
              <Controller
                name={`placeDelivery`}
                control={control}
                render={({field: {onChange, value}}) => (
                  <ProcessTextField
                    name={`placeDelivery`}
                    placeholder="Local de entrega"
                    value={value ?? ""}
                    onChange={onChange}
                    size="small"
                    label="Local de entrega"
                  />
                )}
              />
            </Grid>
          )}

          {shouldShowTransportCompany(modalVoyage as Voyage) && (
            <Grid size={3}>
              <Controller
                name={`transportCompany.person`}
                control={control}
                render={({field: {onChange, value}}) => (
                  <CustomAutocomplete
                    optionSecondaryContent
                    fullWidth
                    textFieldProps={{
                      label: "Companhia",
                    }}
                    name={
                      `transportCompany.person` as `transportCompany.person`
                    }
                    placeholder="Companhia"
                    tooltipText={getTooltipText(value?.name ?? "")}
                    loading={isLoadingTransportCompanyOptions}
                    options={mountOptions(value, transportCompanyOptions)}
                    value={value?.name ?? null}
                    onOpen={() =>
                      getTransportCompanyOptions(
                        setTransportCompanyOptions,
                        1,
                        search,
                      )
                    }
                    onInputChange={(e, value) => {
                      if (e !== null) {
                        setSearch(value);
                        getTransportCompanyOptions(
                          setTransportCompanyOptions,
                          1,
                          value,
                        );
                      }
                    }}
                    getNextPage={() => {
                      onBottomScrollTransportCompany(
                        setTransportCompanyOptions,
                        search,
                      );
                    }}
                    onChange={(e, value) => {
                      handleChangeAutocomplete(
                        e,
                        value,
                        `transportCompany.person`,
                      );
                    }}
                    isOptionEqualToValue={(option, value) =>
                      value === option?.label
                    }
                  />
                )}
              />
            </Grid>
          )}

          {shouldShowObservation(modalVoyage as Voyage) && (
            <Grid size={3}>
              <Controller
                name={`observation`}
                control={control}
                render={({field: {onChange, value}}) => (
                  <ProcessTextField
                    name={`observation`}
                    placeholder="Observação"
                    value={value ?? ""}
                    onChange={onChange}
                    size="small"
                    label="Observação"
                  />
                )}
              />
            </Grid>
          )}
        </Grid>

        <DialogActions sx={{padding: `0 ${pxToRem(20)} ${pxToRem(20)} 0`}}>
          <CustomButton
            size="medium"
            variant="outlined"
            onClickFn={handleCloseModal}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            size="medium"
            onClickFn={handleFormSubmissionWithValidation}
            endIcon="save"
            form={formId}
          >
            Salvar
          </CustomButton>
        </DialogActions>
      </form>
    </CustomModal>
  );
};
export default AddOrEditVoyageModal;
